package com.fireavert.administration.yardi_integration_page.yardi_main_page.client.presentation

import YardiSavePropertyDataRequest
import com.fireavert.administration.yardi_integration_page.presentation.YardiCommonDataScreen
import com.fireavert.common.Try
import com.fireavert.management_companies.client.ManagementCompanyRepository
import com.fireavert.yardi.common_data.client.YardiCommonDataClientRepository
import com.fireavert.yardi.common_data.data_models.YardiResidentStatusEnum
import com.fireavert.yardi.common_data.data_models.requests.client_requests.*
import com.fireavert.yardi.service_requests.data_models.requests.client_requests.YardiClientServiceRequestCreateEditRequest
import com.fireavert.yardi.service_requests.data_models.requests.client_requests.YardiClientServiceRequestGetRequest
import kotlinx.datetime.LocalDate

class YardiCommonDataInteractor(
    private val repository: YardiCommonDataClientRepository,
    private val managementCompanyRepository: ManagementCompanyRepository,
    private val screen: YardiCommonDataScreen
) {

    suspend fun onLoad() {
        screen.loading = true

        val credentials = when (val maybe = repository.getYardiCredentialsList()) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }

        screen.availableCredentials = credentials

        val managementCommpanies = when (val maybe = managementCompanyRepository.getAllManagementCompanies()) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }

        screen.managementCompanyList = managementCommpanies
    }

    suspend fun savePropertyData(request: YardiSavePropertyDataRequest) {
        screen.loading = true

        when (val maybe = repository.savePropertyDataBase(request)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }
    }

    suspend fun testGetVersionNumber(credentialsId: Int){
        if (credentialsId == -1) {
            screen.error = "Please select a credentials"
            return
        }
        screen.loading = true

        val result = when (val maybe = repository.testVersionNumber(credentialsId)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }
        val resultSplit = result.split("v")
        val voyagerVersion = resultSplit[0]
        val splitTwo = resultSplit[1].split("_")
        val interfacesPluginVersion = splitTwo[0]
        val interfacesTypeVersion = splitTwo[1]
        screen.data = formatDataString("Version: ${result}\nVoyager Version: " +
                "$voyagerVersion\nInterfaces Plugin Version: $interfacesPluginVersion\n" +
                "Interfaces Type Version: $interfacesTypeVersion")
        screen.loading = false
    }

    suspend fun testPing(credentialsId: Int){
        screen.loading = true

        val result = when (val maybe = repository.testPing(credentialsId)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testGetResidentData(credentialsId: Int, propertyId: String){
        screen.loading = true

        val result = when (val maybe = repository.testGetResidentData(
            YardiClientPropertyIdRequest(
                credentialsId,
                propertyId
            )
        )) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testGetResidentByStatusData(credentialsId: Int,
                                            propertyId: String,
                                            residentStatus: YardiResidentStatusEnum,
                                            dateTo: LocalDate?,
                                            dateFrom: LocalDate?) {
        val request: YardiClientResidentByStatusRequest
        println("Date to: $dateTo")
        println("Date from: $dateFrom")
        screen.loading = true
        if (dateFrom == null || dateTo == null) {
            println("Date is null")
            request = YardiClientResidentByStatusRequest(
                credentialsId, propertyId, residentStatus
            )
        }
        else {
            println("Date is not null")
            request = YardiClientResidentByStatusRequest(
                credentialsId, propertyId, residentStatus, dateTo.toString(), dateFrom.toString()
            )
        }

        val result = when (val maybe = repository.testGetResidentByStatusData(request)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }

        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testGetResidentDataResidentialOnly(credentialsId: Int, propertyId: String, tenantCode: String){
        screen.loading = true

        val result = when (val maybe = repository.testGetResidentDataResidentialOnly(
            YardiClientGetResidentialDataResidentialOnlyRequest(credentialsId, propertyId, tenantCode)
        )) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testGetPropertyList(credentialsId: Int, propertyId: String){
        screen.loading = true

        val result = when (val maybe = repository.yardiGetPropertyList(
            YardiClientPropertyIdRequest(
                credentialsId,
                propertyId
            )
        )) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                println("Error: ${maybe.exception.message}")
                screen.error = maybe.exception.message.toString()
                screen.data = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }
        screen.data = result.toString()
        screen.loading = false
    }

    suspend fun testGetResidentsByUnit(credentialsId: Int, propertyId: String, unitId: String){
        screen.loading = true

        val result = when (val maybe = repository.testGetResidentsByUnit(
            YardiClientGetResidentByUnitRequest(
                credentialsId,
                propertyId,
                unitId
            )
        )) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testGetTenantStatus(credentialsId: Int, propertyId: String){
        screen.loading = true

        val result = when (val maybe = repository.testGetTenantStatus(
            YardiClientPropertyIdRequest(
                credentialsId,
                propertyId
            )
        )) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testGetRateLimits(credentialsId: Int){
        screen.loading = true

        val result = when (val maybe = repository.testGetRateLimits(credentialsId)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                println("Error: ${maybe.exception.message}")
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }

        screen.data = formatDataString(result.toString())
        screen.loading = false
    }

    suspend fun testGetUnitAttachments(credentialsId: Int){
        screen.loading = true

        val result = when (val maybe = repository.testGetUnitAttachments(credentialsId)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }




    suspend fun testGetUnitData(credentialsId: Int, propertyId: String){
        screen.loading = true
        val result = when(val maybe = repository.yardiGetUnitData(
            YardiClientPropertyIdRequest(
                credentialsId,
                propertyId
            )
        )){
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.toString()
                return
            }
        }
        val units = result.units
        screen.data = units.joinToString(separator = "\n") { unit ->
            "Unit ID: ${unit.unitId}, Unit Name: ${unit.marketingName}, " +
            "Unit Type: ${unit.unitType}, Unit Status: ${unit.identificationValues}"
        }
        screen.loading = false
    }

    suspend fun testGetServiceRequestVersionNumber(credentialsId: Int) {
        screen.loading = true
        val result = when (val maybe = repository.testGetServiceRequestVersionNumber(
            YardiClientCredentialRequest(
                credentialsId
            )
        )) {
            is Try.Success -> maybe.value.response
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.orEmpty()
                screen.data = ""
                return
            }
        }
        val resultSplit = result.split("v")
        val voyagerVersion = resultSplit[0]
        val splitTwo = resultSplit[1].split("_")
        val interfacesPluginVersion = splitTwo[0]
        val interfacesTypeVersion = splitTwo[1]
        screen.data = formatDataString("Version: ${result}\nVoyager Version: " +
                "$voyagerVersion\nInterfaces Plugin Version: $interfacesPluginVersion\n" +
                "Interfaces Type Version: $interfacesTypeVersion")
        screen.loading = false
    }

    suspend fun testGetServiceRequestPropertyConfigurations(
        credentialsId: Int,
        propertyId: String
    ) {
        screen.loading = true
        val req = YardiClientPropertyIdRequest(credentialsId, propertyId)
        val result = when (val maybe = repository.testGetServiceRequestPropertyConfigurations(req)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.orEmpty()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result.response)
        screen.loading = false
    }

    suspend fun getPropertyConfigurations(
        credentialsId: Int,
        propertyId: String
    ) {
        screen.loading = true
        val req = YardiClientPropertyIdRequest(credentialsId, propertyId)
        val result = when (val maybe = repository.getPropertyConfigurations(req)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.orEmpty()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result)
        screen.loading = false
    }

    suspend fun testCreateEditServiceRequest(
        serviceRequestId: String? = "",
        credentialsId: Int,
        propertyId: String,
        unitId: String?,
        tenantId: String?,
        vendorId: String?,
        name: String?,
        description: String?,
        fullDescription: String?,
        accessNotes: String?,
        origin: String? = "",
        roomCode: String? = "",
        priority: String? = "",
        category: String? = "",
        subCategory: String? = "",
        hasPermissionToEnter: Boolean? = false,
        problemDescriptionNotes: String? = "",
        tenantCaused: Boolean? = false,
        requestorName: String? = "",
        requestorPhoneNumber: String? = "",
        requestorEmail: String? = ""
        // dueDate: String? = null // Uncomment if needed
    ) {
        screen.loading = true
        var newRoomCode = roomCode
        if (newRoomCode == "null" || newRoomCode.isNullOrBlank()) {
            newRoomCode = ""
        }

        val req = YardiClientServiceRequestCreateEditRequest(
            serviceRequestId,
            credentialsId,
            propertyId,
            unitId,
            tenantId,
            vendorId,
            name,
            description,
            accessNotes,
            fullDescription,
            origin,
            newRoomCode,
            priority,
            category,
            subCategory,
            hasPermissionToEnter,
            problemDescriptionNotes,
            tenantCaused,
            requestorName,
            requestorPhoneNumber,
            requestorEmail,
            // dueDate // Uncomment if needed
        )
        val result = when (val maybe = repository.testCreateEditServiceRequest(req)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                println("Error: ${maybe.exception.message}")
                screen.loading = false
                screen.error = maybe.exception.message.orEmpty()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result.response)
        screen.loading = false
    }

    suspend fun testGetServiceRequests(
        credentialsId: Int,
        propertyId: String,
        serviceId: String
    ) {
        screen.loading = true
        val req = YardiClientServiceRequestGetRequest(credentialsId, propertyId, listOf(serviceId.toLong()))
        val result = when (val maybe = repository.testGetServiceRequests(req)) {
            is Try.Success -> maybe.value
            is Try.Error -> {
                screen.loading = false
                screen.error = maybe.exception.message.orEmpty()
                screen.data = ""
                return
            }
        }
        screen.data = formatDataString(result.response)
        screen.loading = false
    }

    fun formatDataString(inputString: String): String {
        // Handle empty or null strings
        if (inputString.isBlank()) return inputString

        val result = StringBuilder()
        var indentLevel = 0
        var inQuotes = false

        inputString.forEach { char ->
            when (char) {
                '{', '[' -> {
                    result.append(char)
                    result.append('\n')
                    indentLevel++
                    result.append("  ".repeat(indentLevel))
                }
                '}', ']' -> {
                    result.append('\n')
                    indentLevel--
                    result.append("  ".repeat(indentLevel))
                    result.append(char)
                }
                ',' -> {
                    result.append(char)
                    if (!inQuotes) {
                        result.append('\n')
                        result.append("  ".repeat(indentLevel))
                    }
                }
                '=' -> {
                    result.append(" = ")
                }
                '"' -> {
                    inQuotes = !inQuotes
                    result.append(char)
                }
                else -> result.append(char)
            }
        }

        return result.toString()
    }

}